/**
 * Where a request is in its route. NUMERIC on the wire — no string enum converter is
 * configured — so this is an `as const` object of the server's own numbers, never a string
 * union, which would typecheck and make every comparison silently false.
 *
 * Accounting is NOT a user of this system: a request at Accounting is only TAGGED, and the
 * Cash Division records the voucher when it comes back.
 */
export const DR_STATUS = {
  cashProcessing: 0,
  atAccounting: 1,
  forRelease: 2,
  disbursed: 3,
  returned: 4,
} as const;

export type DisbursementRequestStatus = (typeof DR_STATUS)[keyof typeof DR_STATUS];

/** One row of the register. It carries the requester's personId and NO name. */
export interface DisbursementRequestListItem {
  id: string;
  requestNumber: string;
  status: DisbursementRequestStatus;
  statusLabel: string;
  requesterPersonnelId: string;
  /** Omitted from the wire where the personnel row is gone — read with `!= null`. */
  requesterPersonId?: string | null;
  transactionTypeName: string;
  paymentTypeName: string;
  /** The SUM of the live lines, computed by the server on the read. */
  amount: number;
  /** The live lines' particulars, joined in order — the row's title. */
  particulars: string;
  itemCount: number;
  voucherNumber?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface DisbursementRequestDetail {
  id: string;
  requestNumber: string;
  status: DisbursementRequestStatus;
  statusLabel: string;
  requesterPersonnelId: string;
  requesterPersonId?: string | null;
  transactionTypeId: string;
  transactionTypeName: string;
  paymentTypeId: string;
  paymentTypeName: string;
  /** The live lines, in order. */
  items: DisbursementRequestItem[];
  /** The total — the server's sum over `items`, never sent back. */
  amount: number;
  notes?: string | null;
  isEditable: boolean;

  cashProcessedAt?: string | null;
  cashProcessedByName?: string | null;

  voucherNumber?: string | null;
  voucherDate?: string | null;
  voucherReceivedAt?: string | null;
  voucherReceivedByName?: string | null;
  /** The person who took the voucher back — an id, resolved to a name on the page. */
  voucherReceivedByPersonnelId?: string | null;
  voucherReceivedByPersonId?: string | null;

  disbursedOn?: string | null;
  disbursementReference?: string | null;
  disbursedAt?: string | null;
  disbursedByName?: string | null;

  returnReason?: string | null;
  returnedFromStatus?: DisbursementRequestStatus | null;
  returnedFromStatusLabel?: string | null;
  returnedAt?: string | null;
  returnedByName?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface DisbursementRequestListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  transactionType?: string;
  paymentType?: string;
}

export interface DisbursementRequestItem {
  id: string;
  particulars: string;
  amount: number;
  sortOrder: number;
}

/**
 * One line on the wire. No id and no position — a correction replaces the lines, and the
 * position is the array order. EVERY row on screen is sent, blanks included, so a rejection's
 * index is the row the reader sees; the server drops the blanks.
 */
export interface DisbursementRequestItemInput {
  particulars: string;
  amount: number | null;
}

/** Create and correct. The number, the status, the total and every stamp are the server's. */
export interface DisbursementRequestRequest {
  requesterPersonnelId: string | null;
  transactionTypeId: string | null;
  paymentTypeId: string | null;
  items: DisbursementRequestItemInput[];
  notes?: string | null;
}

/**
 * The permission key that moves a request on from each stage — and therefore also governs
 * RETURNING it from there. Mirrors `DisbursementRequestStatuses.StepAction` on the server,
 * which is what actually refuses.
 */
export function stepPermission(status: DisbursementRequestStatus): string | null {
  switch (status) {
    case DR_STATUS.cashProcessing:
      return 'disbursement-requests.process';
    case DR_STATUS.atAccounting:
      return 'disbursement-requests.receive-voucher';
    case DR_STATUS.forRelease:
      return 'disbursement-requests.release';
    default:
      return null;
  }
}

/** Whether a request may still be returned — while it is with Cash or at Accounting. */
export function isReturnable(status: DisbursementRequestStatus): boolean {
  return status === DR_STATUS.cashProcessing || status === DR_STATUS.atAccounting;
}
